import {
  type Syllable,
  isSyllable,
  readSyllable,
  toneFromMarks,
  writeSyllable,
  writeSyllableSpelling,
} from "@kensio/pinyinjs";

import { toneMarked } from "./fuxi-tone-numbers.js";

/**
 * Which syllable a keystroke in a pinyin field has just finished, if any.
 *
 * `./fuxi-syllable-sounds.ts` plays every syllable a reader finishes, whatever
 * the card asked for, so a reader hears what they wrote before they send it.
 * This half is the language and that half is the sound, the line
 * `./fuxi-tone-numbers.ts` draws against `./fuxi-pinyin.ts` next door.
 *
 * **A tone is what finishes a syllable.** A digit does it at once, and so does
 * a tone button, which writes one. A syllable typed with its mark on is
 * finished once it cannot grow any further: `bìng` is finished on the `g`, and
 * `bì` waits, because the reader may be on the way to `bìn` or `bìng`. It is
 * finished by whatever comes next and could not continue it, so `bìj` sounds
 * `bì` and `bìn` sounds nothing yet.
 *
 * **A syllable with no tone written finishes nothing.** That covers a reader
 * still typing `bing` before the digit, and it is also why the neutral tone is
 * heard only where a reader types the 5. A bare `zi` is the same keys as the
 * start of `zi3`, and nothing in the field says which the reader meant.
 *
 * **A syllable that has sounded is never read again.** What the field held
 * up to the caret when the last syllable sounded is handed back, and only the
 * text after it is searched. Without that, `bi4` would become `bì` and the
 * `n` of a following `ni3` would make it `bìn`, a longer syllable the reader
 * never typed, and a digit's `jiān` would sound a second time when the next
 * letter ended it by the rule above.
 */

/** A syllable a keystroke finished. */
export interface FinishedSyllable {
  /**
   * The syllable as the recordings are keyed: lower case, tone number last,
   * `ü` written `v`. `bing4`, `lv3`, `zi5`.
   */
  readonly key: string;

  /**
   * The field up to the caret once the keystroke has been taken, digits
   * written as marks. What the caller hands back as `sounded` next time.
   */
  readonly through: string;
}

/** The longest syllable this looks for, which is `zhuang` and a digit. */
const longest = 7;

/** A trailing run of letters with no tone written on them. */
const plainRun = /[a-zü:v]+$/iu;

/** A trailing run of anything a syllable can be spelt with, marks included. */
const letterRun = /[\p{L}:]+$/u;

/** What can follow a syllable's spelling inside a longer one. */
const continuations = ["a", "e", "i", "o", "u", "n", "g"];

/** The key a syllable's recording is filed under. */
function keyOf(syllable: Syllable): string {
  return writeSyllable({ ...syllable, erhua: false }, "numbers").replace(
    /ü/gu,
    "v",
  );
}

/**
 * Whether a finished-looking syllable could still become a longer one.
 *
 * One letter more is enough to ask. Every syllable that has a longer one
 * starting with it also has one exactly a letter longer, `ji` having `jia` on
 * the way to `jiang` and `ju` having `jue` on the way to `juan`.
 */
function grows(syllable: Syllable): boolean {
  const spelling = writeSyllableSpelling({ ...syllable, erhua: false });

  return continuations.some((next) => isSyllable(`${spelling}${next}`));
}

/**
 * The tone-marked syllable the text ends on, where it ends on one.
 *
 * The longest ending that reads as a syllable with a mark on it, so `jiānbì`
 * ends on `bì` and `bìng` on `bìng` rather than on `ng`.
 */
function markedEnding(
  text: string,
): { text: string; syllable: Syllable } | undefined {
  const run = letterRun.exec(text)?.[0] ?? "";

  for (let size = Math.min(run.length, longest); size > 0; size--) {
    const ending = run.slice(-size);
    const syllable = readSyllable(ending.toLowerCase());

    if (syllable !== undefined && toneFromMarks(ending) !== undefined) {
      return { text: ending, syllable };
    }
  }

  return undefined;
}

/** The numbered syllable a digit at the end of the text finished. */
function numberedEnding(text: string): Syllable | undefined {
  const digit = text.slice(-1);
  const run = plainRun.exec(text.slice(0, -1))?.[0] ?? "";

  for (let size = Math.min(run.length, longest - 1); size > 0; size--) {
    const syllable = readSyllable(`${run.slice(-size)}${digit}`.toLowerCase());

    if (syllable?.tone !== undefined) {
      return syllable;
    }
  }

  return undefined;
}

/**
 * The syllable this keystroke finished, if it finished one.
 *
 * @param typed - The field up to the caret as the keystroke left it, before
 *   any digit in it has been written as a mark.
 * @param sounded - What `through` was the last time a syllable in this field
 *   sounded, or nothing.
 * @param pressed - Whether a tone button wrote the mark rather than a key.
 *   A press says the syllable is finished, so `jiān` sounds then rather than
 *   waiting to see whether it grows into `jiāng`.
 * @returns The syllable, or nothing where the keystroke finished none.
 */
export function finishedSyllable(
  typed: string,
  sounded: string | undefined,
  pressed = false,
): FinishedSyllable | undefined {
  const text = typed.normalize("NFC");

  if (/[1-5]$/u.test(text)) {
    const numbered = numberedEnding(text);

    return numbered === undefined
      ? undefined
      : { key: keyOf(numbered), through: toneMarked(text) };
  }

  const fresh =
    sounded !== undefined && text.startsWith(sounded)
      ? text.slice(sounded.length)
      : text;
  const ending = markedEnding(fresh);

  if (ending !== undefined && (pressed || !grows(ending.syllable))) {
    return { key: keyOf(ending.syllable), through: text };
  }

  const before = fresh.slice(0, -1);
  const ended = markedEnding(before);

  if (ended === undefined) {
    return undefined;
  }

  const carried = fresh.slice(before.length - ended.text.length).toLowerCase();
  const continues =
    letterRun.test(carried) && readSyllable(carried) !== undefined;

  return continues
    ? undefined
    : { key: keyOf(ended.syllable), through: text.slice(0, -1) };
}
